<script>
  import { onMount, createEventDispatcher, getContext } from 'svelte'
  import { scale } from 'svelte/transition'
  import Tooltip from '@/components/core/extensions/tooltip/v1.0.0/Tooltip.svelte'

  const platform = getContext('platform')
  const dispatch = createEventDispatcher()

  export let id = ''
  export let title = ''
  export let dismissable = true
  export let disablePadding = false
  export let showAdvancedToggle = false
  export let widthClass = ''
  export let showAside = false
  export let style = ''
  export let tabs = []

  let showAdvanced = false
  let activeTab = ''

  function tabChange () {
    dispatch('tabChange', activeTab)
  }

  function toggleShowAdvanced () {
    showAdvanced = !showAdvanced
    dispatch('showAdvanced')
  }

  onMount(async () => {
    await require('@/styles/vars.css')

    emitEvent('showAdvancedToggle', showAdvanced)

    if (tabs.length) {
      activeTab = tabs[0]
      tabChange()
    }
  })

  function internalClose () {
    if (!dismissable) {
      return
    }

    dispatch('close')
    platform.api.browser.modal(id).close()
  }

  function emitEvent (event, e) {
    dispatch(event, e)
  }
</script>

<div {id} class="cms-modal">
  <div class="modal-overlay" on:click={() => internalClose()} />
  <div
    class="cms-modal__container {widthClass}"
    transition:scale={{ start: 0.9, duration: 0.2 }}
    on:dragover={e => emitEvent('dragover', e)}
    on:drop={e => emitEvent('drop', e)}
    on:dragend={e => emitEvent('dragend', e)}
  >
    <div class="cms-modal__top-bar">
      <h2>{title}</h2>
      <div class="flex">
        {#if showAdvancedToggle}
          <Tooltip tip="Show advanced options" bottom>
            <button class="cms-modal__top-bar__button" on:click={() => toggleShowAdvanced()}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                <path d="M440-120v-240h80v80h320v80H520v80h-80Zm-320-80v-80h240v80H120Zm160-160v-80H120v-80h160v-80h80v240h-80Zm160-80v-80h400v80H440Zm160-160v-240h80v80h160v80H680v80h-80Zm-480-80v-80h400v80H120Z"/>
              </svg>
            </button>
          </Tooltip>
        {/if}

        {#if dismissable}
          <Tooltip tip="Close modal window" bottom>
            <button class="cms-modal__top-bar__button" on:click={() => internalClose()}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000">
                <path d="M0 0h24v24H0V0z" fill="none"/><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
              </svg>
            </button>
          </Tooltip>
        {/if}
      </div>
    </div>

    {#if showAside}
      <div class="cms-modal__aside absolute left-0 top-0 h-full w-6/12 shadow-md bg-gray-100 z-50 p-6">
        <slot name="aside" />
      </div>

      <div class="absolute left-0 top-0 h-full w-full bg-black opacity-70 z-40" />
    {/if}

    {#if tabs.length}
      <div class="tabs flex gap-0 border-b border-gray-200 my-3 mx-8">
        {#each tabs as tab}
          <div class="transition-all cursor-pointer px-3 pb-3 {activeTab === tab ? '!border-b-blue-500' : 'border-b-transparent'} border-b-2 border-b-transparent single-tab text-md font-bold" on:click={() => { activeTab = tab; tabChange() }}>
            {tab}
          </div>
        {/each}
      </div>
    {/if}

    <div class="cms-modal__body" style="{disablePadding ? 'padding: 0;' : ''} {style}" on:scroll={e => emitEvent('scroll', e)}>
      <slot name="body"></slot>
    </div>

    <div class="cms-modal__bottom-bar">
      <slot name="toggles"></slot>
    </div>
  </div>
</div>

<style lang="scss">:global(.cms-modal--open) {
  display: flex !important;
}
:global(.cms-modal--open) .cms-modal__container {
  display: block;
}

.cms-modal {
  z-index: 50;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 66px 16px 16px 66px;
}

.modal-overlay {
  position: fixed;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
}

.cms-modal__body {
  max-height: calc(100vh - 275px);
  min-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding: 16px;
}

.cms-modal__container {
  max-height: calc(100vh - 120px);
  transition: all 0.3s ease;
  position: relative;
  background: white;
  margin-left: auto;
  margin-right: auto;
  border-radius: 10px;
  overflow: hidden;
  max-width: 1000px;
  display: none;
}

@media (min-width: 992px) {
  .cms-modal__container {
    min-width: 800px;
    padding-bottom: 80px;
  }
}
.cms-modal__top-bar {
  padding: 16px 16px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.cms-modal__top-bar h2 {
  margin: 0;
  font-weight: bolder;
  font-size: 25px;
}

.cms-modal__top-bar__button {
  margin-left: 8px;
  width: 35px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  border-radius: 99999999999px;
  flex: none;
  background: transparent;
}
.cms-modal__top-bar__button:hover {
  background: rgba(0, 0, 0, 0.1);
}

.cms-modal__bottom-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}</style>
<script context="module">export const svelteCompilerVersion = {"major":4,"minor":2,"patch":18};</script>